import * as Sentry from "@sentry/react";

const dsn = import.meta.env.VITE_SENTRY_DSN;
const environment =
  import.meta.env.VITE_SENTRY_ENVIRONMENT ||
  (import.meta.env.PROD ? "production" : "development");

const SENSITIVE_KEY =
  /email|password|token|authorization|cookie|secret|api[_-]?key|guest|subscriber|session/i;

function scrubValue(key: string, value: unknown): unknown {
  if (SENSITIVE_KEY.test(key)) return "[Filtered]";
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return scrubObject(value as Record<string, unknown>);
  }
  if (typeof value === "string" && value.includes("@")) return "[Filtered]";
  return value;
}

function scrubObject(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    out[key] = scrubValue(key, value);
  }
  return out;
}

function beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  if (event.user) {
    delete event.user.email;
    delete event.user.ip_address;
    delete event.user.username;
  }
  if (event.request) {
    delete event.request.cookies;
    if (event.request.headers && typeof event.request.headers === "object") {
      event.request.headers = scrubObject(
        event.request.headers as Record<string, unknown>,
      ) as typeof event.request.headers;
    }
    if (event.request.data && typeof event.request.data === "object") {
      event.request.data = scrubObject(event.request.data as Record<string, unknown>);
    }
  }
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((crumb) => {
      if (!crumb.data) return crumb;
      return {
        ...crumb,
        data: scrubObject(crumb.data as Record<string, unknown>),
      };
    });
  }
  return event;
}

Sentry.init({
  dsn,
  enabled: Boolean(dsn),
  environment,
  sendDefaultPii: false,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 0,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: import.meta.env.PROD ? 0.25 : 0,
  beforeSend,
  ignoreErrors: [
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
  ],
});

function isSentryConsoleTestEnabled(): boolean {
  if (import.meta.env.DEV) return true;
  try {
    return localStorage.getItem("pickle_sentry_debug") === "1";
  } catch {
    return false;
  }
}

if (dsn && isSentryConsoleTestEnabled()) {
  window.triggerSentryTest = (throwUncaught = false) => {
    const err = new Error("Pickle event — Sentry test (console)");
    if (throwUncaught) throw err;
    const eventId = Sentry.captureException(err);
    console.info("[sentry:pickle-event] Test error sent.", { eventId, environment });
    return eventId;
  };

  window.triggerSentryTestMessage = (level: "info" | "warning" | "error" = "info") => {
    const eventId = Sentry.captureMessage(
      "Pickle event — Sentry test message (console)",
      level,
    );
    console.info("[sentry:pickle-event] Test message sent.", {
      eventId,
      environment,
      level,
    });
    return eventId;
  };

  if (import.meta.env.DEV) {
    console.info(
      "[sentry:pickle-event] triggerSentryTest() | triggerSentryTest(true) | triggerSentryTestMessage()",
    );
  }
}

declare global {
  interface Window {
    /** Dev, or prod after localStorage pickle_sentry_debug=1 + reload. */
    triggerSentryTest?: (throwUncaught?: boolean) => string | undefined;
    triggerSentryTestMessage?: (
      level?: "info" | "warning" | "error",
    ) => string | undefined;
  }
}

/** Report API failures; skips expected auth errors (wrong email, etc.). */
export function captureApiFailure(
  operation: "auth" | "roster" | "restore",
  error: unknown,
  context?: Record<string, string>,
): void {
  if (!dsn) return;
  const message = error instanceof Error ? error.message : String(error);
  if (
    message.includes("guest list") ||
    message.includes("invalid_email") ||
    message.includes("not_on_guest_list")
  ) {
    return;
  }
  Sentry.withScope((scope) => {
    scope.setTag("pickle.api", operation);
    if (context) {
      for (const [k, v] of Object.entries(context)) {
        scope.setExtra(k, v);
      }
    }
    if (error instanceof Error) {
      Sentry.captureException(error);
    } else {
      Sentry.captureMessage(`pickle-event api: ${operation} — ${message}`, "error");
    }
  });
}

export { Sentry };
